import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2, Webhook } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { RoleGate } from '@/components/RoleGate';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateWebhook } from '@/features/integrations/hooks/useCreateWebhook';
import { useDeleteWebhook } from '@/features/integrations/hooks/useDeleteWebhook';
import { useUpdateWebhook } from '@/features/integrations/hooks/useUpdateWebhook';
import {
  type CreateWebhookInput,
  type WebhookEvent,
  createWebhookSchema,
  webhookEvents,
} from '@/lib/schemas';
import { type WebhookRow, listWebhooksQuery } from '@/lib/supabase/queries/webhooks';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/$wsSlug/settings/integrations')({
  component: IntegrationsSettingsPage,
});

const EVENT_LABELS: Record<WebhookEvent, string> = {
  'work_item.created': 'Item creado',
  'work_item.updated': 'Item actualizado',
  'work_item.archived': 'Item archivado',
  'comment.created': 'Comentario nuevo',
  'cycle.completed': 'Ciclo completado',
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('es', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ---- Webhook Form Modal ----

interface WebhookFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

function WebhookFormModal({ open, onOpenChange, workspaceId }: WebhookFormModalProps) {
  const create = useCreateWebhook(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Omit<CreateWebhookInput, 'workspace_id'>>({
    resolver: zodResolver(createWebhookSchema.omit({ workspace_id: true })),
    defaultValues: { name: '', url: '', events: [], secret: '', enabled: true },
  });

  const selectedEvents = watch('events') ?? [];

  function toggleEvent(ev: WebhookEvent) {
    if (selectedEvents.includes(ev)) {
      setValue(
        'events',
        selectedEvents.filter((e) => e !== ev),
        { shouldValidate: true },
      );
    } else {
      setValue('events', [...selectedEvents, ev], { shouldValidate: true });
    }
  }

  async function onSubmit(values: Omit<CreateWebhookInput, 'workspace_id'>) {
    await create.mutateAsync(values);
    reset();
    onOpenChange(false);
  }

  function handleClose() {
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose} maxWidth="36rem">
      <DialogHeader>
        <DialogTitle>Agregar webhook</DialogTitle>
        <DialogDescription>
          MEIDEI enviara un POST a la URL configurada cuando ocurran los eventos seleccionados.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Name */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="wh-name">Nombre</Label>
          <Input id="wh-name" placeholder="Mi webhook" {...register('name')} />
          {errors.name && <p className="text-xs text-critical">{errors.name.message}</p>}
        </div>

        {/* URL */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="wh-url">URL de destino</Label>
          <Input id="wh-url" placeholder="https://..." type="url" {...register('url')} />
          {errors.url && <p className="text-xs text-critical">{errors.url.message}</p>}
        </div>

        {/* Events */}
        <div className="flex flex-col gap-1.5">
          <Label>Eventos</Label>
          <div className="grid grid-cols-2 gap-2">
            {webhookEvents.map((ev) => {
              const checked = selectedEvents.includes(ev);
              return (
                <button
                  key={ev}
                  type="button"
                  onClick={() => toggleEvent(ev)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors',
                    checked
                      ? 'border-accent bg-accent-sf text-accent font-medium'
                      : 'border-border bg-surface text-body hover:bg-surface-elev',
                  )}
                  aria-pressed={checked}
                >
                  <span
                    className={cn(
                      'w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center',
                      checked ? 'bg-accent border-accent' : 'border-border',
                    )}
                  >
                    {checked && (
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path
                          d="M1 3l2 2 4-4"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  {EVENT_LABELS[ev]}
                </button>
              );
            })}
          </div>
          {errors.events && <p className="text-xs text-critical">{errors.events.message}</p>}
        </div>

        {/* Secret */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="wh-secret">
            Secreto HMAC <span className="text-mute font-normal">(opcional)</span>
          </Label>
          <Input
            id="wh-secret"
            placeholder="Clave para firmar el payload"
            type="password"
            autoComplete="off"
            {...register('secret')}
          />
          {errors.secret && <p className="text-xs text-critical">{errors.secret.message}</p>}
        </div>

        {/* Enabled toggle */}
        <div className="flex items-center gap-2">
          <input
            id="wh-enabled"
            type="checkbox"
            className="accent-accent w-4 h-4"
            {...register('enabled')}
            defaultChecked
          />
          <Label htmlFor="wh-enabled" className="cursor-pointer">
            Activar inmediatamente
          </Label>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting || create.isPending}>
            {create.isPending ? 'Guardando...' : 'Crear webhook'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ---- Webhook Row ----

interface WebhookItemProps {
  webhook: WebhookRow;
  workspaceId: string;
}

function WebhookItem({ webhook, workspaceId }: WebhookItemProps) {
  const deleteWebhook = useDeleteWebhook(workspaceId);
  const updateWebhook = useUpdateWebhook(workspaceId);

  function handleToggle() {
    void updateWebhook.mutate({ id: webhook.id, enabled: !webhook.enabled });
  }

  function handleDelete() {
    if (confirm(`Eliminar webhook "${webhook.name}"?`)) {
      void deleteWebhook.mutate(webhook.id);
    }
  }

  return (
    <div className="flex items-start gap-3 px-4 py-3 bg-surface hover:bg-surface-elev transition-colors">
      {/* Icon */}
      <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-accent-sf flex items-center justify-center">
        <Webhook size={15} className="text-accent" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-ink">{webhook.name}</span>
          <span
            className={cn(
              'text-[10px] rounded px-1.5 py-0.5 font-medium',
              webhook.enabled
                ? 'bg-[color:var(--cat-success)]/10 text-[color:var(--cat-success)]'
                : 'bg-surface-canvas text-mute',
            )}
          >
            {webhook.enabled ? 'Activo' : 'Inactivo'}
          </span>
        </div>
        <p className="text-xs text-quiet truncate mt-0.5">{webhook.url}</p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {webhook.events.map((ev) => (
            <span
              key={ev}
              className="text-[10px] bg-surface-canvas text-quiet border border-border rounded px-1.5 py-0.5"
            >
              {EVENT_LABELS[ev as WebhookEvent] ?? ev}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-mute mt-1">Creado {formatDate(webhook.created_at)}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={handleToggle}
          disabled={updateWebhook.isPending}
          title={webhook.enabled ? 'Desactivar' : 'Activar'}
          className="text-xs px-2 py-1 rounded border border-border text-quiet hover:text-body hover:bg-surface-elev transition-colors disabled:opacity-50"
        >
          {webhook.enabled ? 'Desactivar' : 'Activar'}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteWebhook.isPending}
          title="Eliminar webhook"
          className="p-1.5 rounded text-mute hover:text-critical transition-colors disabled:opacity-50"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ---- Main Page ----

function IntegrationsSettingsPage() {
  const { wsSlug } = Route.useParams();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: workspace } = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const workspaceId = workspace?.id ?? '';

  const { data: webhooks = [], isLoading } = useQuery(listWebhooksQuery(workspaceId));

  if (!workspaceId || isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-quiet text-sm">Cargando...</div>
    );
  }

  return (
    <RoleGate wsSlug={wsSlug}>
      <div className="max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-base font-semibold text-ink">Webhooks salientes</h2>
            <p className="text-sm text-quiet">
              Webhooks salientes hacia sistemas externos (Slack, n8n, Zapier...). Cada webhook se
              dispara cuando ocurre un evento como "tarea creada" o "comentario nuevo".
            </p>
          </div>
          <Button size="sm" onClick={() => setModalOpen(true)}>
            <Plus size={14} />
            Agregar webhook
          </Button>
        </div>

        {/* Webhooks list */}
        <div className="flex flex-col divide-y divide-border-soft border border-border rounded-lg overflow-hidden">
          {webhooks.map((wh) => (
            <WebhookItem key={wh.id} webhook={wh} workspaceId={workspaceId} />
          ))}

          {webhooks.length === 0 && (
            <div className="px-4 py-10 flex flex-col items-center gap-2 text-center">
              <Webhook size={28} className="text-mute" />
              <p className="text-sm text-quiet">No hay webhooks configurados todavia.</p>
              <p className="text-xs text-mute max-w-xs">
                Agrega uno para recibir notificaciones en tiempo real en tus sistemas externos.
              </p>
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="mt-4 rounded-lg border border-border-soft bg-surface-canvas p-4 text-xs text-quiet">
          <p className="font-medium text-body mb-1">Como funciona</p>
          <p>
            MEIDEI enviara un HTTP POST con un payload JSON al endpoint configurado cada vez que
            ocurra un evento seleccionado. Si configuraste un secreto HMAC, el header{' '}
            <code className="font-mono bg-surface border border-border rounded px-1">
              X-MEIDEI-Signature
            </code>{' '}
            llevara la firma para verificar la autenticidad.
          </p>
        </div>

        {/* Modal */}
        <WebhookFormModal open={modalOpen} onOpenChange={setModalOpen} workspaceId={workspaceId} />
      </div>
    </RoleGate>
  );
}
